import { Fragment, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { extractApiError } from "@/lib/api-error";

import { updateAdminStatus } from "../api/adminsApi";
import type { Admin, UpdateAdminStatusAction } from "../types";

type AdminStatusModalProps = {
    isOpen: boolean;
    onClose: () => void;
    admin: Admin | null;
    action: UpdateAdminStatusAction | null;
    onCompleted?: (message: string) => void;
};

const ACTION_META: Record<
    UpdateAdminStatusAction,
    {
        title: string;
        description: string;
        buttonLabel: string;
        buttonClassName: string;
    }
> = {
    suspend: {
        title: "Suspend Admin",
        description:
            "This admin will lose access until a super admin reactivates the account.",
        buttonLabel: "Confirm Suspend",
        buttonClassName: "bg-amber-600 hover:bg-amber-700",
    },
    deactivate: {
        title: "Deactivate Admin",
        description:
            "This admin account will be marked inactive and must be activated again before use.",
        buttonLabel: "Confirm Deactivate",
        buttonClassName: "bg-slate-700 hover:bg-slate-800",
    },
    activate: {
        title: "Activate Admin",
        description: "This admin account will be restored to active status immediately.",
        buttonLabel: "Confirm Activate",
        buttonClassName: "bg-emerald-600 hover:bg-emerald-700",
    },
    reactivate: {
        title: "Reactivate Admin",
        description: "This admin account will be restored to active status immediately.",
        buttonLabel: "Confirm Reactivate",
        buttonClassName: "bg-emerald-600 hover:bg-emerald-700",
    },
};

export const AdminStatusModal = ({
    isOpen,
    onClose,
    admin,
    action,
    onCompleted,
}: AdminStatusModalProps) => {
    const queryClient = useQueryClient();
    const [reason, setReason] = useState("");
    const [error, setError] = useState("");

    const actionMeta = useMemo(() => {
        if (!action) return null;
        return ACTION_META[action];
    }, [action]);

    useEffect(() => {
        if (!isOpen) {
            setReason("");
            setError("");
        }
    }, [isOpen]);

    const statusMutation = useMutation({
        mutationFn: () => {
            if (!admin?.adminId || !action) {
                throw new Error("No admin action selected.");
            }

            return updateAdminStatus(admin.adminId, {
                action,
                reason: reason.trim() || undefined,
            });
        },
        onSuccess: async (response) => {
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: ["admins"] }),
                admin
                    ? queryClient.invalidateQueries({
                          queryKey: ["adminDetails", admin.adminId],
                      })
                    : Promise.resolve(),
            ]);
            onClose();
            setReason("");
            setError("");
            onCompleted?.(response.message || "Admin status updated successfully.");
        },
        onError: (mutationError) => {
            setError(extractApiError(mutationError));
        },
    });

    const handleClose = () => {
        if (statusMutation.isPending) return;
        onClose();
        setReason("");
        setError("");
    };

    if (!admin || !action || !actionMeta) {
        return null;
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-slate-900">
                                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5 dark:border-slate-800">
                                    <div>
                                        <Dialog.Title className="text-lg font-semibold text-slate-900 dark:text-white">
                                            {actionMeta.title}
                                        </Dialog.Title>
                                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                                            {admin.fullName} ({admin.email})
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="rounded-lg p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>

                                <div className="space-y-4 px-6 py-5">
                                    <div className="flex gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                                        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                                        <p>{actionMeta.description}</p>
                                    </div>

                                    <div>
                                        <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                                            Reason <span className="text-xs text-slate-400">(optional)</span>
                                        </label>
                                        <textarea
                                            rows={4}
                                            value={reason}
                                            onChange={(event) => {
                                                setReason(event.target.value);
                                                if (error) setError("");
                                            }}
                                            placeholder={`Add a short note for this ${action} action...`}
                                            className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/15 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                                        />
                                    </div>

                                    {error ? (
                                        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
                                            {error}
                                        </div>
                                    ) : null}
                                </div>

                                <div className="flex gap-3 border-t border-slate-200 px-6 py-5 dark:border-slate-800">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={handleClose}
                                        disabled={statusMutation.isPending}
                                        className="flex-1"
                                    >
                                        Cancel
                                    </Button>
                                    <button
                                        type="button"
                                        onClick={() => statusMutation.mutate()}
                                        disabled={statusMutation.isPending}
                                        className={`flex-1 rounded-md px-4 py-2.5 text-sm font-medium text-white transition disabled:cursor-not-allowed disabled:opacity-65 ${actionMeta.buttonClassName}`}
                                    >
                                        {statusMutation.isPending ? "Updating..." : actionMeta.buttonLabel}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
